// Admin user detail (/admin/users/:id) — profile, role management, manual
// entitlement grant (support path), UU PDP account-data erasure, plus the
// user's licenses and orders.

import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fieldErrorClass } from "@ahcut/ui/recipes";
import {
  Badge,
  Button,
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
  Field,
} from "@/ui";
import { Loading, ErrorState } from "@/components/States";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { FilterSelect } from "@/components/FilterSelect";
import { LicenseAdminActions } from "@/components/LicenseAdminActions";
import { MachineBinding } from "@/components/MachineBinding";
import { useAsync } from "@/lib/useAsync";
import { useAuth } from "@/lib/auth";
import { eraseAdminUser, getAdminUser, grantAdminLicense, setAdminUserRole } from "@/lib/admin";
import {
  formatDate,
  formatDateTime,
  formatRupiah,
  licenseStateLabel,
  licenseStateTone,
  orderStatusLabel,
  orderStatusTone,
  roleLabel,
  tierLabel,
  tierOptions,
} from "@/lib/format";
import type { AdminUserDetail, PaidTierId } from "@/lib/types";
import { AdminPanel, PanelBody, PanelEmpty } from "@/components/admin/AdminPanel";
import { DataCell, DataRow, DataTable, NoValue, type DataColumn } from "@/components/admin/DataTable";
import { DetailHeader } from "@/components/DetailHeader";
import { dataCellClass, dataIdClass, factsGridClass } from "@/components/admin/classes";
import { BreakableText } from "@/components/admin/BreakableText";

const GRANT_TIER_OPTIONS = tierOptions({ paidOnly: true });

const ROLE_HINT_ID = "admin-user-role-hint";

const LICENSE_COLUMNS: readonly DataColumn[] = [
  { label: "Terbit" },
  { label: "Tier" },
  { label: "Status" },
  { label: "Perangkat" },
  { label: "Aksi" },
];

const ORDER_COLUMNS: readonly DataColumn[] = [
  { label: "Waktu" },
  { label: "Tier" },
  { label: "Jumlah", align: "end" },
  { label: "Status" },
  { label: "Detail" },
];

type PendingAction = "role" | "grant" | "erase" | null;

/** True once the account's PII has been anonymized (UU PDP erasure) — the
 *  server blanks the email or rewrites it to `erased-*@erased.invalid`. */
function isErased(email: string): boolean {
  return email === "" || email.endsWith("@erased.invalid");
}

export function AdminUserDetailPage() {
  const { id = "" } = useParams();
  const { user: self } = useAuth();

  const { data, loading, error, reload } = useAsync<AdminUserDetail>(
    (s) => getAdminUser(id, s),
    [id],
  );

  const [pending, setPending] = useState<PendingAction>(null);
  const [grantTier, setGrantTier] = useState<string>("pro");
  const [eraseReason, setEraseReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  if (loading && !data) return <Loading label="Memuat pengguna…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!data) return <ErrorState error={new Error("Pengguna tidak ditemukan.")} onRetry={reload} />;

  const u = data.user;
  const isSelf = self?.id === u.id;
  const nextRole = u.role === "admin" ? "user" : "admin";
  const erased = isErased(u.email);
  const displayName = u.email || "Akun dianonimkan";
  // Disabled buttons take no pointer events, so the reason is also shown as text.
  const roleLocked = isSelf && nextRole === "user";

  function open(action: Exclude<PendingAction, null>) {
    setActionError(null);
    setEraseReason("");
    setPending(action);
  }

  async function runPending() {
    setBusy(true);
    setActionError(null);
    try {
      if (pending === "role") {
        await setAdminUserRole(u.id, nextRole);
      } else if (pending === "grant") {
        await grantAdminLicense(u.id, grantTier as PaidTierId);
      } else if (pending === "erase") {
        await eraseAdminUser(u.id, eraseReason);
      }
      setPending(null);
      reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <DetailHeader
        backTo="/admin/users"
        backLabel="Semua pengguna"
        title={displayName}
        hint={roleLocked ? "Tidak bisa mencabut admin sendiri." : undefined}
        hintId={ROLE_HINT_ID}
        actions={
          <>
            <Button
              variant="quiet"
              size="sm"
              onClick={() => open("role")}
              disabled={roleLocked}
              aria-describedby={roleLocked ? ROLE_HINT_ID : undefined}
              title={
                roleLocked
                  ? "Tidak bisa mencabut admin sendiri"
                  : `Ubah role menjadi ${roleLabel(nextRole)}`
              }
            >
              {u.role === "admin" ? "Cabut akses admin" : "Jadikan admin"}
            </Button>
            <Button variant="quiet" size="sm" onClick={() => open("grant")}>
              Grant lisensi manual
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => open("erase")}
              disabled={erased}
              title={
                erased
                  ? "Data akun sudah dianonimkan"
                  : "Anonimkan PII akun ini (UU PDP) — ireversibel"
              }
            >
              Hapus data akun
            </Button>
          </>
        }
      />

      <AdminPanel>
        <PanelBody>
          <DescriptionList className={factsGridClass}>
            <DescriptionTerm>Email</DescriptionTerm>
            <DescriptionDetails className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="min-w-0 break-all">{u.email || <NoValue label="Email dihapus" />}</span>
              {erased && (
                <Badge tone="crit" dot>
                  Data dihapus
                </Badge>
              )}
            </DescriptionDetails>
            <DescriptionTerm>Role</DescriptionTerm>
            <DescriptionDetails>
              <Badge tone={u.role === "admin" ? "pro" : "neutral"}>{roleLabel(u.role)}</Badge>
            </DescriptionDetails>
            <DescriptionTerm>Terdaftar</DescriptionTerm>
            <DescriptionDetails className={dataIdClass}>{formatDate(u.created_at)}</DescriptionDetails>
            <DescriptionTerm>Kode referral</DescriptionTerm>
            <DescriptionDetails className={dataIdClass}>
              {u.referred_by_code ?? <NoValue label="Tanpa kode referral" />}
            </DescriptionDetails>
            <DescriptionTerm>ID</DescriptionTerm>
            <DescriptionDetails className={dataIdClass}>
              <BreakableText text={u.id} at="id" />
            </DescriptionDetails>
          </DescriptionList>
        </PanelBody>
      </AdminPanel>

      <AdminPanel title="Lisensi">
        {data.licenses.length === 0 ? (
          <PanelEmpty>Belum ada lisensi.</PanelEmpty>
        ) : (
          <DataTable label="Lisensi pengguna" columns={LICENSE_COLUMNS} stackBelow="58rem">
            {data.licenses.map((l) => (
              <DataRow key={l.id}>
                <DataCell label="Terbit" className={dataCellClass}>
                  {formatDate(l.issued_at)}
                </DataCell>
                <DataCell label="Tier">{tierLabel(l.tier)}</DataCell>
                <DataCell label="Status">
                  <Badge tone={licenseStateTone(l.revoked_at)} dot>
                    {licenseStateLabel(l.revoked_at)}
                  </Badge>
                </DataCell>
                <DataCell label="Perangkat" className="whitespace-nowrap">
                  <MachineBinding machineId={l.machine_id} />
                </DataCell>
                <DataCell actions>
                  <LicenseAdminActions license={l} onDone={reload} />
                </DataCell>
              </DataRow>
            ))}
          </DataTable>
        )}
      </AdminPanel>

      <AdminPanel title="Pesanan">
        {data.orders.length === 0 ? (
          <PanelEmpty>Belum ada pesanan.</PanelEmpty>
        ) : (
          <DataTable label="Pesanan pengguna" columns={ORDER_COLUMNS} stackBelow="40rem">
            {data.orders.map((o) => (
              <DataRow key={o.id}>
                <DataCell label="Waktu" className={dataCellClass}>
                  {formatDateTime(o.created_at)}
                </DataCell>
                <DataCell label="Tier">{tierLabel(o.tier)}</DataCell>
                <DataCell label="Jumlah" align="end" className={`${dataCellClass} text-fg-0`}>
                  {formatRupiah(o.amount)}
                </DataCell>
                <DataCell label="Status">
                  <Badge tone={orderStatusTone(o.status)} dot>
                    {orderStatusLabel(o.status)}
                  </Badge>
                </DataCell>
                <DataCell label="Detail">
                  <Link to={`/admin/orders/${o.id}`}>
                    Lihat<span className="sr-only"> pesanan {formatDateTime(o.created_at)}</span>
                  </Link>
                </DataCell>
              </DataRow>
            ))}
          </DataTable>
        )}
      </AdminPanel>

      <ConfirmDialog
        open={pending === "role"}
        title={u.role === "admin" ? "Cabut akses admin?" : "Jadikan admin?"}
        confirmLabel={u.role === "admin" ? "Cabut akses" : "Jadikan admin"}
        danger={u.role === "admin"}
        busy={busy}
        onConfirm={runPending}
        onCancel={() => setPending(null)}
      >
        <p>
          Role <strong className="font-medium break-all text-fg-0">{displayName}</strong> diubah
          menjadi <strong className="font-medium text-fg-0">{roleLabel(nextRole)}</strong>.
          Perubahan berlaku seketika (otorisasi dicek per permintaan).
        </p>
        {actionError && (
          <p role="alert" className={fieldErrorClass}>
            {actionError}
          </p>
        )}
      </ConfirmDialog>

      <ConfirmDialog
        open={pending === "grant"}
        title="Grant lisensi manual?"
        confirmLabel="Grant lisensi"
        busy={busy}
        onConfirm={runPending}
        onCancel={() => setPending(null)}
      >
        <p>
          Entitlement diberikan tanpa pembayaran (jalur support — mis. resolusi pembayaran
          manual). Lisensi paid aktif sebelumnya (bila ada) otomatis dicabut.
        </p>
        <FilterSelect
          label="Tier"
          value={grantTier}
          options={GRANT_TIER_OPTIONS}
          onChange={setGrantTier}
        />
        {actionError && (
          <p role="alert" className={fieldErrorClass}>
            {actionError}
          </p>
        )}
      </ConfirmDialog>

      <ConfirmDialog
        open={pending === "erase"}
        title="Hapus data akun?"
        confirmLabel="Hapus data akun"
        danger
        busy={busy}
        onConfirm={runPending}
        onCancel={() => setPending(null)}
      >
        <p>
          Menghapus PII akun ini permanen: email, nama, identitas Google dikosongkan; semua sesi
          dicabut. Pesanan &amp; lisensi tetap tersimpan untuk pembukuan.{" "}
          <strong className="font-medium text-fg-0">Tidak bisa dibatalkan.</strong>
        </p>
        <Field
          label="Alasan (opsional)"
          value={eraseReason}
          onChange={(e) => setEraseReason(e.target.value)}
          placeholder="mis. permintaan penghapusan via support"
        />
        {actionError && (
          <p role="alert" className={fieldErrorClass}>
            {actionError}
          </p>
        )}
      </ConfirmDialog>
    </div>
  );
}
